import { useEffect, useRef } from "react";

export default function ScratchCardCanvas({
  children,
  brushSize = 22,
  onComplete,
  finishPercent = 70,
  coverColor = "#D9D9D9",
  coverImage,
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const isDrawing = useRef(false);
  const completed = useRef(false);

  const fillCover = () => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    canvas.width = container.offsetWidth;
    canvas.height = container.offsetHeight;

    const ctx = canvas.getContext("2d");
    ctx.globalCompositeOperation = "source-over";
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (coverImage) {
      const img = new Image();
      img.onload = () => {
        // Draw cover image stretched to the canvas
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      };
      img.onerror = () => {
        ctx.fillStyle = coverColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      };
      img.src = coverImage;
    } else {
      ctx.fillStyle = coverColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // subtle hint (only when using a flat color cover)
      ctx.fillStyle = "rgba(0,0,0,0.35)";
      ctx.font =
        "bold 14px system-ui, -apple-system, Segoe UI, Roboto, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("SCRATCH", canvas.width / 2, canvas.height / 2);
    }
  };

  useEffect(() => {
    fillCover();
    window.addEventListener("resize", fillCover);
    return () => window.removeEventListener("resize", fillCover);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverImage, coverColor]);

  const draw = (e) => {
    if (!isDrawing.current) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, brushSize, 0, 2 * Math.PI);
    ctx.fill();
  };

  const handleStart = (e) => {
    isDrawing.current = true;
    draw(e);
  };

  const handleEnd = () => {
    isDrawing.current = false;
    if (completed.current) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;

    let cleared = 0;
    for (let i = 0; i < imageData.length; i += 4) {
      if (imageData[i + 3] < 128) cleared++;
    }

    const total = canvas.width * canvas.height;
    const percent = (cleared / total) * 100;

    if (percent >= finishPercent) {
      completed.current = true;
      onComplete?.();
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        borderRadius: "9999px",
        overflow: "hidden",
      }}
    >
      <div style={{ position: "absolute", inset: 0, zIndex: 1 }}>{children}</div>

      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 2,
          width: "100%",
          height: "100%",
          touchAction: "none",
          borderRadius: "9999px",
        }}
        onMouseDown={handleStart}
        onMouseMove={draw}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onTouchStart={handleStart}
        onTouchMove={draw}
        onTouchEnd={handleEnd}
      />
    </div>
  );
}


